import { useRef, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams as _useSearchParams } from 'react-router-dom';
import { isInternalBool } from '../../utils/library-helper';
import { logEvent } from 'firebase/analytics';
import { analytics} from '../../firebase';
import {
  ResourceFacets,
  BaseFilterState,
  useResourceFilters,
  useResourceFilterOptions,
} from './use-resource-filters';
import { FilterTopicType } from '../../components/filters/digital-skills-topic-filter';

export interface LibraryFilterState extends BaseFilterState {
  locations: string[];
  hotTopics: string[];
}

const initialLibraryFilters: LibraryFilterState = {
  mediaTypes: [],
  skillCategoryIds: [],
  locations: [],
  hotTopics: [],
  searchQuery: '',
};

/**
 * Parses URL search params and returns filter state that can be applied to the filter hook.
 * This is used to restore filter state from URL on component mount.
 */
function getFiltersFromURLParams(searchParams: URLSearchParams): Partial<LibraryFilterState> {
  const restoredFilters: Partial<LibraryFilterState> = {};

  const mediaTypesParam = searchParams.get('mediaTypes');
  if (mediaTypesParam) {
    restoredFilters.mediaTypes = mediaTypesParam
      .split(',')
      .map((m) => m.trim())
      .filter((m) => m.length > 0);
  }

  const skillCategoriesParam = searchParams.get('skillCategories');
  if (skillCategoriesParam) {
    restoredFilters.skillCategoryIds = skillCategoriesParam
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  const searchQueryParam = searchParams.get('searchQuery');
  if (searchQueryParam !== null) {
    restoredFilters.searchQuery = searchQueryParam;
  }

  const isInternalParam = searchParams.get('isInternal');
  if (isInternalParam !== null) {
    restoredFilters.locations =
      isInternalParam === 'true' ? ['Internal'] : isInternalParam === 'false' ? ['External'] : [];
  }

  const hotTopicsParam = searchParams.get('hotTopics');
  if(hotTopicsParam && hotTopicsParam.length > 0) {
    restoredFilters.hotTopics = hotTopicsParam
      .split(',')
      .map((topic) => topic.trim())
      .filter((topic) => topic.length > 0);
  }

  return restoredFilters;
}

/**
 * Gets the page number from URL params, or 0 if not present.
 */
function getPageNumberFromURL(searchParams: URLSearchParams): number {
  const pageNumberParam = searchParams.get('pageNumber');
  if (!pageNumberParam) {
    return 0;
  }

  const pageNumber = parseInt(pageNumberParam, 10);
  return Number.isNaN(pageNumber) ? 0 : Math.max(pageNumber - 1, 0);
}

// Sync the selected topic IDs from the filter hook with the topic filter UI component, so that the UI displays the correctly selected filters.
// Takes in the selected topicIds from the hook, and topic data from the DB. Returns a type compatible array for the UI component
export const syncSelectedTopicFilters = (topicIds: string[], topics: FilterTopicType[]) => {
    if(topicIds.length > 0) {
        const selectedTopics = topics.filter((topic: FilterTopicType) => topicIds.includes(topic.id));

        return selectedTopics;
    }

    return [];
};

export function useLibraryFiltersWithUrlSync(facets?: ResourceFacets, hotTopicsData?: FilterTopicType[]) {
  const [searchParams, setSearchParams] = _useSearchParams();
  const isInitializedRef = useRef(false);
  const previousFiltersRef = useRef<LibraryFilterState | null>(null);
  const previousPageRef = useRef<number | null>(null);
  const filterOptions = useResourceFilterOptions(facets);

  // Get initial filters from URL if present
  const getInitialFilters = useCallback((): LibraryFilterState => {
    const urlFilters = getFiltersFromURLParams(searchParams);
    return {
      ...initialLibraryFilters,
      ...urlFilters,
    };
  }, [searchParams]);

  // Initialize filter hook with filters from URL if available
  const filterHook = useResourceFilters({
    initialFilters: getInitialFilters(),
    debounceDelay: 300,
  });

  const previousSearchQuery = useRef(filterHook.debouncedFilters.searchQuery);
  const resetFilters = () => {
    filterHook.setIsReset(true);
    filterHook.updateFilters(initialLibraryFilters);
  };

  const handleTopicChange = (changingTopic: FilterTopicType) => {

    const existingTopicFilters = filterHook.filters.hotTopics;

    if (existingTopicFilters.includes(changingTopic.id)) {
      const updatedTopics = existingTopicFilters.filter((topicId: string) => topicId !== changingTopic.id);
      filterHook.updateFilters({hotTopics: updatedTopics});
    } else {
      filterHook.updateFilters({hotTopics: [...existingTopicFilters, changingTopic.id]});

      logEvent(analytics, 'hot_topic_filter_applied', {
        hot_topic_title: changingTopic.title
      });
    }
  };

  const selectedTopics = useMemo(() => {
    if(hotTopicsData){
      return syncSelectedTopicFilters(filterHook.filters.hotTopics, hotTopicsData);
    }
  }, [hotTopicsData, filterHook.filters.hotTopics]);

  // Reset page when debounced search query actually changes
  useEffect(() => {
    if (previousSearchQuery.current !== filterHook.debouncedFilters.searchQuery) {
      filterHook.setIsReset(false);
      filterHook.setCurrentPage(0);
      previousSearchQuery.current = filterHook.debouncedFilters.searchQuery;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterHook.debouncedFilters.searchQuery]);

  // Handle restoration of page number from URL (but only on initial mount)
  useEffect(() => {
    if (!isInitializedRef.current) {
      const urlPageNumber = getPageNumberFromURL(searchParams);
      if (urlPageNumber !== 0) {
        filterHook.setCurrentPage(urlPageNumber);
      }
      isInitializedRef.current = true;
      previousFiltersRef.current = { ...filterHook.filters };
      previousPageRef.current = filterHook.currentPage;
    }
    // Only run once on mount - intentionally empty dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when filters or page changes
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitializedRef.current) {
      return;
    }

    // Check if filters or page have actually changed
    const filtersChanged =
      JSON.stringify(previousFiltersRef.current) !== JSON.stringify(filterHook.filters);
    const pageChanged = previousPageRef.current !== filterHook.currentPage;

    if (!filtersChanged && !pageChanged) {
      return;
    }

    // Build new search params
    const newSearchParams = new URLSearchParams();

    if (filterHook.filters.mediaTypes.length > 0) {
      newSearchParams.set('mediaTypes', filterHook.filters.mediaTypes.join(','));
    }

    if (filterHook.filters.skillCategoryIds.length > 0) {
      newSearchParams.set('skillCategories', filterHook.filters.skillCategoryIds.join(','));
    }

    if (filterHook.filters.searchQuery.length > 0) {
      newSearchParams.set('searchQuery', filterHook.filters.searchQuery);
    }

    if (filterHook.filters.locations.length === 1) {
      newSearchParams.set(
        'isInternal',
        filterHook.filters.locations[0] === 'Internal' ? 'true' : 'false'
      );
    }

    if(filterHook.filters.hotTopics.length > 0) {
      newSearchParams.set('hotTopics', filterHook.filters.hotTopics.join(','));
    }

    if (filterHook.currentPage !== 0) {
      newSearchParams.set('pageNumber', (filterHook.currentPage + 1).toString());
    }

    setSearchParams(newSearchParams, { replace: true });
    previousFiltersRef.current = { ...filterHook.filters };
    previousPageRef.current = filterHook.currentPage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterHook.filters, filterHook.currentPage]);

  const resourceParams = useMemo(() => {
    const pageSize = 20; // Library uses 20 items per page

    return {
      mediaTypes: filterHook.filters.mediaTypes.join(','),
      skillCategories: filterHook.filters.skillCategoryIds.join(','),
      specialisms: filterHook.filters.hotTopics.join(','),
      searchQuery: filterHook.debouncedFilters.searchQuery,
      isInternal:
        filterHook.filters.locations?.length === 1
          ? isInternalBool(filterHook.filters.locations[0])
          : undefined,
      pageNumber: filterHook.currentPage,
      pageSize: pageSize,
    };
  }, [filterHook.filters, filterHook.debouncedFilters, filterHook.currentPage]);

  return {
    ...filterHook,
    ...filterOptions,
    handleTopicChange,
    resetFilters,
    resourceParams,
    selectedTopics,
  };
}
